import { Text, View } from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import type { ReactElement } from 'react';

import Enums from '@core/types/api/Enums';
import type { QuotePriceEscalation } from '@core/types/api/QuotePriceEscalation/QuotePriceEscalation';
import { NullableCurrencyString } from '@core/utils/currency';
import { FormatIsoDateWithLocale } from '@core/utils/dateHelpers';
import { randomUUID } from '@core/utils/uuid';

import {
  type PdfColumnConfig,
  PdfTable,
  type PdfTableProps,
  Styles,
  pdfColumnFromConfig,
} from '../../components/PdfTable';

type QuotePriceEscalationRow = {
  id: string;
  escalationDate: string;
  amount: string;
};

type QuotePriceEscalationColumn = {
  id: keyof QuotePriceEscalationRow;
  label?: string;
  width?: Style['width'];
  headerViewStyle?: Style;
  headerTextStyle?: Style;
  rowViewStyle?: Style;
  rowTextStyle?: Style;
};

const makeHeaderRenderer =
  ({ headerViewStyle = {}, headerTextStyle = {} }: QuotePriceEscalationColumn) =>
  (label: string): ReactElement => (
    <View style={headerViewStyle}>
      <Text style={[Styles.tableCellHeader, headerTextStyle].flat()}>{label}</Text>
    </View>
  );

const makeCellRenderer =
  ({ rowViewStyle = {}, rowTextStyle = {} }: QuotePriceEscalationColumn) =>
  (value: string): ReactElement => (
    <View style={rowViewStyle}>
      <Text style={[Styles.tableCell, rowTextStyle].flat()}>{value}</Text>
    </View>
  );

type PdfPriceEscalationTableProps = {
  quotePriceEscalations: QuotePriceEscalation[];
  columnConfigs: QuotePriceEscalationColumn[];
  locale: string;
} & Omit<PdfTableProps<any>, 'columns' | 'rows'>;

export const PdfPriceEscalationTable = (props: PdfPriceEscalationTableProps): ReactElement => {
  const { quotePriceEscalations, columnConfigs, locale, ...tableProps } = props;

  const rows: QuotePriceEscalationRow[] = quotePriceEscalations
    .sort((qpe1, qpe2) => qpe1.escalationDate.toMillis() - qpe2.escalationDate.toMillis())
    .map((qpe) => {
      const amount =
        qpe.kind === Enums.EscalationKind.Flat
          ? NullableCurrencyString({ cur: qpe.flatChange })
          : `${Number(qpe.changeRatio) * 100}%`;
      const escalationDate = FormatIsoDateWithLocale(qpe.escalationDate, locale);
      return { id: randomUUID(), escalationDate, amount };
    });

  const cols: PdfColumnConfig<QuotePriceEscalationRow>[] = columnConfigs.map(
    (config): PdfColumnConfig<QuotePriceEscalationRow> => ({
      id: config.id,
      // `isDisplayed` is ignored when working with PDF tables
      isDisplayed: true,
      label: config.label ?? '',
      type: 'string',
      widthOverride: config.width,
      formatValueForPdf: makeCellRenderer(config),
      formatColumnHeaderForPdf: makeHeaderRenderer(config),
    }),
  );

  return <PdfTable columns={cols.map(pdfColumnFromConfig)} rows={rows} {...tableProps} />;
};
